// THE TYPED NO-CHANGE VERDICT — why a fix round on a pull request ended without moving
// the head, as a closed vocabulary the harness validates. See `../babysit.ts`.
//
// developerz-ai/wurk#566: the round correctly changed nothing — the failing check was the
// base branch's, whose tip read green without running the suite — and all the driver could
// observe was "a round landed, the head did not move". That is `ci_fix_declined`, a page to
// a human, on a PR whose next step a maintainer would have taken without thinking: fix the
// base. The prose in `BABYSIT_NO_CHANGE_PATH` says which it was, but prose is not something
// the driver may act on (`docs/standards/llm-decides-harness-guarantees.md`).
//
// So the model CHOOSES one of the kinds and fills its evidence, in a JSON file beside the
// prose, and the harness guarantees the shape: a kind outside the vocabulary, a missing or
// blank evidence field, or a field past its bound is REFUSED whole — no partial verdict, no
// default. A refused or absent verdict leaves the round what it was before this file: an
// untyped decline, which a person reads.
//
//   not_this_pr   the failure is not caused by this pull request's diff. Evidence: the
//                 failing check (which the driver re-checks against the head's own red
//                 checks), why the diff is not implicated, and where the cause is.
//   cannot_adapt  a dependency bump that genuinely cannot be adapted. Evidence: which
//                 package(s), and why.
//   pr_body_line  the round found the pull request's BODY needs one `key: value` line (a
//                 repo check reads it, e.g. `note: <uuid>`) and a round cannot edit the
//                 body itself. Evidence: the exact line; the driver (`apps/worker`
//                 `babysit/takeover.ts`) applies it to the body. The line is the one field
//                 here that lands VERBATIM in text GitHub renders, so its schema is a
//                 grammar, not a length: `^[a-z_-]+: [A-Za-z0-9._:/-]+$` admits no markup,
//                 no mention, no second line.
//
// EVERY STRING IS BOUNDED HERE AND NEUTRALISED AT THE BOX (`apps/runner`
// `babysit/round-verdict.ts`), because each one ends up in text another reader takes in: a
// fix ask filed against the base branch, or a comment on the pull request.

import { z } from 'zod';
import { RAW_TASK_EXCERPT_MAX } from '../raw-task/evidence';

/** Where the fix round writes its typed verdict, under the checkout (beside the prose). */
export const BABYSIT_VERDICT_PATH = 'tmp/dz/verdict.json' as const;

/**
 * The longest one evidence field may be. It IS the raw task's excerpt bound, not a number
 * beside it: `not_this_pr`'s `why` and `cause` are filed verbatim as the base fix ask's
 * `evidence.excerpts` (`apps/worker` `babysit/takeover.ts`), and a longer field would
 * commit a raw task its own schema refuses on every later read.
 */
export const ROUND_VERDICT_FIELD_MAX = RAW_TASK_EXCERPT_MAX;
/** The longest check or package name a verdict may name (a GitHub check name is at most 255). */
export const ROUND_VERDICT_NAME_MAX = 255;
/** The most packages a `cannot_adapt` verdict may list — a group this large is not one decision. */
export const ROUND_VERDICT_PACKAGES_MAX = 20;
/** The longest `pr_body_line` line: under 200 characters. */
export const ROUND_VERDICT_BODY_LINE_MAX = 199;
/**
 * The grammar of a `pr_body_line` line: one lowercase key, `: `, one token of URL-safe
 * characters. Anchored with no `m` flag and no whitespace class after the separator, so
 * a newline, a space, `<`, `@` or a backtick can never reach the body.
 */
export const ROUND_VERDICT_BODY_LINE_PATTERN = /^[a-z_-]+: [A-Za-z0-9._:/-]+$/;

const evidence = z.string().trim().min(1).max(ROUND_VERDICT_FIELD_MAX);
const name = z.string().trim().min(1).max(ROUND_VERDICT_NAME_MAX);

export const BabysitRoundVerdictSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('not_this_pr'),
      /** The failing check or job, by the name the brief listed it under. */
      check: name,
      /** Why this pull request's diff is not implicated. */
      why: evidence,
      /** Where the cause is, as far as the round found it (a file, a test, a commit). */
      cause: evidence,
    })
    .strict(),
  z
    .object({
      kind: z.literal('cannot_adapt'),
      /** The package(s) whose new version cannot be adapted to. */
      packages: z.array(name).min(1).max(ROUND_VERDICT_PACKAGES_MAX),
      /** What is incompatible. */
      why: evidence,
    })
    .strict(),
  z
    .object({
      kind: z.literal('pr_body_line'),
      /** The one `key: value` line the body needs, exactly as it is to be written. */
      line: z
        .string()
        .min(1)
        .max(ROUND_VERDICT_BODY_LINE_MAX)
        .regex(ROUND_VERDICT_BODY_LINE_PATTERN),
    })
    .strict(),
]);

export type BabysitRoundVerdict = z.infer<typeof BabysitRoundVerdictSchema>;

/** The `pr_body_line` arm: the body needs this one line and the round could not write it. */
export type BabysitPrBodyLineVerdict = Extract<BabysitRoundVerdict, { kind: 'pr_body_line' }>;

/** The result of validating one verdict: the verdict, or why it was refused. */
export type RoundVerdictParse =
  | { readonly kind: 'verdict'; readonly verdict: BabysitRoundVerdict }
  | { readonly kind: 'refused'; readonly why: string };

/**
 * Validate one verdict as the model wrote it (already JSON-parsed). Total: anything the
 * schema does not accept is `refused` with the first issue's path and message, which names
 * no field VALUE — the refusal is logged, and the value is the untrusted part.
 */
export function parseBabysitRoundVerdict(raw: unknown): RoundVerdictParse {
  const parsed = BabysitRoundVerdictSchema.safeParse(raw);
  if (parsed.success) return { kind: 'verdict', verdict: parsed.data };
  const issue = parsed.error.issues[0];
  const at = issue === undefined || issue.path.length === 0 ? 'verdict' : issue.path.join('.');
  return { kind: 'refused', why: `${at}: ${issue?.code ?? 'invalid'}` };
}
